if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();

      this.isViewer = this.classList.contains('product-media-modal--fit');
      if (!this.isViewer) return;

      this.activeIndex = -1;
      this.frame = null;
      this.currentSlide = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onContentChange = this.onContentChange.bind(this);
      this.wheelAccum = 0;
      this.wheelLockedUntil = 0;
      this.wheelResetTimer = null;
      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Variant changes replace the modal content (product-info.js).
      this.contentObserver = new MutationObserver(this.onContentChange);

      this.scroller = this.querySelector('.product-media-modal__content');
      this.prevButton = this.querySelector('.product-media-modal__arrow--prev');
      this.nextButton = this.querySelector('.product-media-modal__arrow--next');
      this.counter = this.querySelector('.product-media-modal__counter');

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.scroller.addEventListener('wheel', this.onWheel, { passive: false });
      this.prevButton?.addEventListener('click', () => {
        if (this.prevButton.getAttribute('aria-disabled') !== 'true') this.step(-1);
      });
      this.nextButton?.addEventListener('click', () => {
        if (this.nextButton.getAttribute('aria-disabled') !== 'true') this.step(1);
      });
    }

    connectedCallback() {
      if (this.isViewer) this.contentObserver.observe(this.scroller, { childList: true });
    }

    disconnectedCallback() {
      if (!this.isViewer) return;
      document.removeEventListener('keydown', this.onKeydown);
      this.desktopQuery.removeEventListener('change', this.onBreakpoint);
      this.contentObserver.disconnect();
      this.clearTimers();
    }

    clearTimers() {
      clearTimeout(this.wheelResetTimer);
      this.wheelResetTimer = null;
      this.wheelAccum = 0;
      this.wheelLockedUntil = 0;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    hide() {
      if (this.isViewer) {
        document.removeEventListener('keydown', this.onKeydown);
        this.desktopQuery.removeEventListener('change', this.onBreakpoint);
        this.clearTimers();
      }
      super.hide();
      window.pauseAllMedia();
    }

    show(opener) {
      if (this.isViewer) this.syncVisibility();
      super.show(opener);
      this.showActiveMedia();

      if (!this.isViewer) return;
      document.addEventListener('keydown', this.onKeydown);
      this.desktopQuery.addEventListener('change', this.onBreakpoint);
      this.activeIndex = -1;
      this.updateNav();
    }

    showActiveMedia() {
      this.querySelectorAll(`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`).forEach((element) => {
          element.classList.remove('is-active');
        }
      )
      const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.classList.add('is-active');

      if (this.isViewer) {
        // Each slide is centered in its own viewport-sized cell.
        activeMedia.scrollIntoView({ block: 'center', inline: 'center' });
      } else {
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
      }

      if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        activeMedia.loadContent();
    }

    // A modal slide is visible when it is displayed and its counterpart in the
    // main gallery (same media id, prefixed with the section id) is displayed
    // too, so both totals always match. Never decided by size: lazy images that
    // have not loaded yet must still count. The content is replaced on variant
    // change, so the list is always read live.
    isShown(el) {
      // Slides hidden by syncVisibility() carry data-viewer-hidden; their own hidden
      // state says nothing about the filters, so only the counterpart decides.
      if (!el.hasAttribute('data-viewer-hidden') && (el.hidden || getComputedStyle(el).display === 'none')) return false;
      const sectionId = this.id.replace(/^ProductModal-/, '');
      const mediaId = el.dataset.mediaId;
      if (!mediaId) return true;
      const counterpart = document.getElementById(`Slide-${sectionId}-${mediaId}`);
      if (!counterpart) return true;
      // On desktop the thumbnail_slider layout hides every inactive slide, so computed
      // display cannot tell a filtered slide from an inactive one. Mirror the variant
      // filters the gallery uses instead (section-main-product.css).
      if (counterpart.hidden) return false;
      if (counterpart.classList.contains('product__media-item--variant') && counterpart !== counterpart.parentElement.firstElementChild) return false;
      if (counterpart.hasAttribute('data-gang-option') && !counterpart.classList.contains('gang__active')) return false;
      return true;
    }

    // Filtered slides are really hidden, so the cells on screen are the cells counted.
    syncVisibility() {
      Array.from(this.scroller.children).forEach((el) => {
        const shown = this.isShown(el);
        el.hidden = !shown;
        el.toggleAttribute('data-viewer-hidden', !shown);
      });
    }

    onContentChange() {
      this.syncVisibility();
      this.activeIndex = -1;
      this.updateNav();
    }

    // Re-centre the current slide when the layout flips between rows and columns.
    onBreakpoint() {
      if (!this.hasAttribute('open')) return;
      const slide = this.currentSlide;
      requestAnimationFrame(() => {
        if (slide && slide.isConnected && !slide.hidden) slide.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
        this.updateNav();
      });
    }

    get slides() {
      return Array.from(this.scroller.children).filter((el) => this.isShown(el));
    }

    // Load the current and adjacent photos right away; the rest stay lazy.
    preloadAround(slides, index) {
      for (let i = index - 1; i <= index + 1; i++) {
        slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      }
    }

    onWheel(event) {
      if (!this.hasAttribute('open') || !this.desktopQuery.matches) return;
      // Leave browser zoom (Ctrl/Meta + wheel), horizontal and empty deltas alone.
      if (event.ctrlKey || event.metaKey || !event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      event.preventDefault();

      // Pixels, lines (Firefox wheels) or pages.
      let delta = event.deltaY;
      if (event.deltaMode === 1) delta *= 40;
      else if (event.deltaMode === 2) delta *= this.scroller.clientHeight;

      // Trackpad inertia keeps firing after the step: every event that arrives while
      // locked extends the lock, which only ends after a quiet spell.
      const now = Date.now();
      if (now < this.wheelLockedUntil) {
        this.wheelLockedUntil = now + 250;
        return;
      }

      this.wheelAccum += delta;
      clearTimeout(this.wheelResetTimer);
      this.wheelResetTimer = setTimeout(() => { this.wheelAccum = 0; }, 200);

      if (Math.abs(this.wheelAccum) < 40) return;
      const direction = this.wheelAccum > 0 ? 1 : -1;
      this.wheelAccum = 0;
      this.wheelLockedUntil = now + 250;
      this.step(direction);
    }

    currentIndex(slides) {
      const box = this.scroller.getBoundingClientRect();
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      let best = 0;
      let bestDistance = Infinity;
      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - cx) + Math.abs(rect.top + rect.height / 2 - cy);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateNav();
      });
    }

    updateNav() {
      if (!this.hasAttribute('open')) return;
      const slides = this.slides;
      if (!slides.length) return;
      const index = this.currentIndex(slides);
      this.currentSlide = slides[index];

      if (this.activeIndex !== -1 && index !== this.activeIndex && typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
      }
      this.activeIndex = index;
      this.preloadAround(slides, index);

      const single = slides.length < 2;
      this.setArrowState(this.prevButton, single, index <= 0, this.nextButton);
      this.setArrowState(this.nextButton, single, index >= slides.length - 1, this.prevButton);
      if (this.counter) {
        this.counter.hidden = single;
        this.counter.querySelector('.product-media-modal__counter-current').textContent = index + 1;
        this.counter.querySelector('.product-media-modal__counter-total').textContent = slides.length;
      }
    }

    // aria-disabled keeps the arrow focusable. When the focused arrow becomes
    // unavailable, focus moves on so Escape (keyup on the modal) keeps working.
    setArrowState(button, hidden, disabled, opposite) {
      if (!button) return;
      button.hidden = hidden;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      if ((hidden || disabled) && document.activeElement === button) {
        const usable = opposite && !opposite.hidden && opposite.getAttribute('aria-disabled') !== 'true';
        (usable ? opposite : this.querySelector('[role="dialog"]'))?.focus();
      }
    }

    step(direction) {
      this.syncVisibility();
      const slides = this.slides;
      const target = slides[Math.min(Math.max(this.currentIndex(slides) + direction, 0), slides.length - 1)];
      const behavior = this.reducedMotionQuery.matches ? 'auto' : 'smooth';
      if (target) target.scrollIntoView({ behavior, block: 'center', inline: 'center' });
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target instanceof Element && event.target.closest('deferred-media, product-model, video, iframe, input, select, textarea, [contenteditable]')) return;
      let direction = 0;
      if (['ArrowRight', 'ArrowDown', 'PageDown'].includes(event.key)) direction = 1;
      else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) direction = -1;
      if (!direction) return;
      event.preventDefault();
      this.step(direction);
    }
  });
}
